// hooks/useSpeechMarkPlayer.ts
import React, { useCallback, useRef, useState } from "react"

import { RiveRef } from "rive-react-native"
import { NodeType, TSentence, TViseme, TWord } from "types/speechMarks"

import { getRiveInput, INPUT_NAMES } from "../utils/pollyVisemeTable"

interface CurrentViseme {
  value: string
  time: number
  interpTime: number
  riveInput: string
}

interface CaptionSegment {
  startTime: number
  endTime: number
  line1: string
  line2: string
  wordIndices: number[]
}

interface CaptionText {
  line1: string
  line2: string
}

const easingFunctions = {
  EaseInOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  EaseInQuad: (t: number) => t * t,
  EaseOutQuad: (t: number) => t * (2 - t),
  Linear: (t: number) => t,
}

// MOBILE CC SETTINGS
export const MAX_CHARS_PER_LINE = 32
const MIN_DISPLAY_TIME_MS = 1000
const CAPTION_LEAD_MS = 0
const CAPTION_LAG_MS = 800

export default function useSpeechMarkPlayer(rive: RiveRef | null) {
  const [isAnimating, setIsAnimating] = useState(false)
  const [currentViseme, setCurrentViseme] = useState<CurrentViseme | null>(null)
  const [captionText, setCaptionText] = useState<CaptionText>({ line1: "", line2: "" })
  const [isCaptionActive, setIsCaptionActive] = useState(false)

  const scheduledTimeoutsRef = useRef<number[]>([])
  const activeAnimationRef = useRef<number | null>(null)
  const animationStartTimeRef = useRef<number>(0)
  const currentTargetViseme = useRef<string>("CLOSE")
  const captionIntervalRef = useRef<number | null>(null)
  const captionStartTimeRef = useRef<number>(0)
  const captionSegmentsRef = useRef<CaptionSegment[]>([])
  const currentSegmentIndexRef = useRef<number>(0)
  const lastCaptionTextRef = useRef<{ line1: string; line2: string }>({ line1: "", line2: "" })

  const riveRef = useRef<RiveRef | null>(rive)

  // Clear all pending timeouts and animation so no setInputState runs after unmount or when rive is gone (e.g. logout).
  // Prevents crash in RiveReactNativeViewManager.setNumberState when the native view is deallocated.
  const clearAllPendingRiveWork = useCallback(() => {
    scheduledTimeoutsRef.current.forEach(clearTimeout)
    scheduledTimeoutsRef.current = []
    if (activeAnimationRef.current !== null) {
      clearTimeout(activeAnimationRef.current)
      activeAnimationRef.current = null
    }
    riveRef.current = null
  }, [])

  React.useEffect(() => {
    riveRef.current = rive
  }, [rive])

  // When rive becomes null (e.g. Rive unmounting on logout), cancel all pending work so we never call setInputState on a deallocated view.
  React.useEffect(() => {
    if (!rive) {
      clearAllPendingRiveWork()
    }
  }, [rive, clearAllPendingRiveWork])

  React.useEffect(() => {
    if (rive) {
      console.log("Rive instance available - initializing to CLOSE state")
      try {
        INPUT_NAMES.forEach((inputName) => {
          rive.setInputState("State Machine 1", inputName, inputName === "CLOSE" ? 100 : 0)
        })
        currentTargetViseme.current = "CLOSE"
        console.log("Rive initialized to CLOSE state successfully")
      } catch (error) {
        console.warn("Failed to initialize Rive to CLOSE state:", error)
      }
    }
  }, [rive])

  // On unmount (e.g. logout), clear all timeouts and animation so no callback runs after the Rive native view is deallocated.
  React.useEffect(() => {
    return () => {
      clearAllPendingRiveWork()
    }
  }, [clearAllPendingRiveWork])

  // ===== CAPTION UTILITY FUNCTIONS =====

  const tokenizeWithPunctuation = useCallback((text: string): string[] => {
    return text.split(/\s+/).filter((w) => w.length > 0)
  }, [])

  /**
   * Split tokens into exactly two lines
   */
  const splitIntoTwoLines = useCallback(
    (tokens: string[]): { line1: string; line2: string; line1WordCount: number } | null => {
      const fullText = tokens.join(" ")

      // If it all fits on one line
      if (fullText.length <= MAX_CHARS_PER_LINE) {
        return { line1: fullText, line2: "", line1WordCount: tokens.length }
      }

      // Try to split into two lines with pyramid style (line1 >= line2)
      let bestSplit = -1
      let bestDifference = Infinity

      // Try different split points to find the most balanced pyramid
      for (let i = Math.floor(tokens.length / 2); i < tokens.length; i++) {
        const line1Tokens = tokens.slice(0, i + 1)
        const line2Tokens = tokens.slice(i + 1)

        const line1Text = line1Tokens.join(" ")
        const line2Text = line2Tokens.join(" ")

        // Both lines must fit
        if (line1Text.length > MAX_CHARS_PER_LINE || line2Text.length > MAX_CHARS_PER_LINE) {
          continue
        }

        // Pyramid style: line1 should be longer or equal to line2
        if (line1Text.length < line2Text.length) {
          continue
        }

        // Find the split with the most balanced lengths (but still pyramid)
        const difference = line1Text.length - line2Text.length
        if (difference < bestDifference) {
          bestDifference = difference
          bestSplit = i
        }
      }

      if (bestSplit === -1) {
        // Couldn't find a valid split, doesn't fit in two lines
        return null
      }

      // Use best split found
      const line1Tokens = tokens.slice(0, bestSplit + 1)
      const line2Tokens = tokens.slice(bestSplit + 1)

      const line1Text = line1Tokens.join(" ")
      const line2Text = line2Tokens.join(" ")

      return {
        line1: line1Text,
        line2: line2Text,
        line1WordCount: line1Tokens.length,
      }
    },
    [],
  )

  /**
   * Create caption segments from sentence and word marks
   */
  const prepareCaptions = useCallback(
    (speechMarks: Array<TSentence | TWord | TViseme>): CaptionSegment[] => {
      const wordMarks = speechMarks.filter((m): m is TWord => m.type === NodeType.WORD)
      const sentenceMarks = speechMarks.filter((m): m is TSentence => m.type === NodeType.SENTENCE)

      if (sentenceMarks.length === 0 || wordMarks.length === 0) {
        console.log("[CAPTIONS] No sentence or word marks found")
        return []
      }

      console.log("[CAPTIONS] Processing sentences:", sentenceMarks.length)

      const segments: CaptionSegment[] = []

      sentenceMarks.forEach((sentence, sentenceIdx) => {
        const wordsInSentence = wordMarks.filter(
          (w) => w.start >= sentence.start && w.end <= sentence.end,
        )

        if (wordsInSentence.length === 0) {
          console.warn("[CAPTIONS] No words found for sentence:", sentence.value)
          return
        }

        const tokens = tokenizeWithPunctuation(sentence.value)

        // console.log("[CAPTIONS] Sentence tokens:", tokens)
        // console.log("[CAPTIONS] Word marks:", wordsInSentence.length)

        let tokenIndex = 0
        let wordIndex = 0

        while (tokenIndex < tokens.length && wordIndex < wordsInSentence.length) {
          let segmentTokens: string[] = []
          let segmentWordIndices: number[] = []
          let result = null

          // Greedy approach: try to fit as many words as possible
          let testTokenIndex = tokenIndex

          while (testTokenIndex < tokens.length) {
            const testTokens = tokens.slice(tokenIndex, testTokenIndex + 1)
            const testResult = splitIntoTwoLines(testTokens)

            if (testResult === null) {
              // Can't fit any more, use previous result
              break
            }

            // This fits, save it and try adding more
            result = testResult
            segmentTokens = testTokens
            segmentWordIndices = Array.from({ length: testTokens.length }, (_, i) => wordIndex + i)
            testTokenIndex++
          }

          // If we couldn't fit anything at all (single word too long), force it anyway
          if (result === null || segmentTokens.length === 0) {
            const singleToken = tokens[tokenIndex]
            // console.warn(
            //  `[CAPTIONS] Single token too long (${singleToken.length} chars): "${singleToken}"`,
            // )

            result = {
              line1: singleToken,
              line2: "",
              line1WordCount: 1,
            }
            segmentTokens = [singleToken]
            segmentWordIndices = [wordIndex]
            tokenIndex++
            wordIndex++
          } else {
            tokenIndex += segmentTokens.length
            wordIndex += segmentTokens.length
          }

          const { line1, line2 } = result

          // Determine timing
          const firstWord = wordsInSentence[segmentWordIndices[0]]
          const lastWord = wordsInSentence[segmentWordIndices[segmentWordIndices.length - 1]]

          // Safety check: if words are missing, fall back to sentence time or previous segment end
          if (!firstWord || !lastWord) {
            console.warn("[CAPTIONS] Missing word marks for segment:", {
              line1,
              line2,
              wordIndex,
              wordsAvailable: wordsInSentence.length,
            })

            const startTime =
              segments.length > 0 ? segments[segments.length - 1].endTime : sentence.time
            const endTime = startTime + MIN_DISPLAY_TIME_MS

            segments.push({
              startTime,
              endTime,
              line1,
              line2,
              wordIndices: segmentWordIndices,
            })
            continue
          }

          const startTime = Math.max(0, firstWord.time - CAPTION_LEAD_MS)

          let endTime: number
          const nextWordIndex = segmentWordIndices[segmentWordIndices.length - 1] + 1

          if (nextWordIndex < wordsInSentence.length) {
            endTime = wordsInSentence[nextWordIndex].time
          } else {
            const nextSentence = sentenceMarks[sentenceIdx + 1]
            if (nextSentence) {
              endTime = nextSentence.time
            } else {
              endTime = lastWord.time + CAPTION_LAG_MS
            }
          }

          endTime = Math.max(endTime, startTime + MIN_DISPLAY_TIME_MS)

          segments.push({
            startTime,
            endTime,
            line1,
            line2,
            wordIndices: segmentWordIndices,
          })

          // console.log(`[CAPTIONS] Segment ${segments.length}:`, {
          //  line1: `"${line1}" (${line1.length} chars)`,
          //  line2: `"${line2}" (${line2.length} chars)`,
          //  pyramid: line1.length >= line2.length ? "✓" : "✗",
          //  startTime: `${startTime}ms`,
          //  endTime: `${endTime}ms`,
          //  duration: `${endTime - startTime}ms`,
          //  wordCount: segmentWordIndices.length,
          // })
        }
      })

      return segments
    },
    [tokenizeWithPunctuation, splitIntoTwoLines],
  )

  // Helper to update caption text only when changed
  const updateCaptionText = useCallback((line1: string, line2: string) => {
    if (lastCaptionTextRef.current.line1 !== line1 || lastCaptionTextRef.current.line2 !== line2) {
      lastCaptionTextRef.current = { line1, line2 }
      setCaptionText({ line1, line2 })
    }
  }, [])

  const captionLoop = useCallback(() => {
    const elapsed = Date.now() - captionStartTimeRef.current
    const segments = captionSegmentsRef.current
    const currentIndex = currentSegmentIndexRef.current

    if (currentIndex >= segments.length) {
      updateCaptionText("", "")
      setIsCaptionActive(false)
      if (captionIntervalRef.current) {
        clearInterval(captionIntervalRef.current)
        captionIntervalRef.current = null
      }
      return
    }

    const currentSegment = segments[currentIndex]

    if (elapsed >= currentSegment.startTime && elapsed < currentSegment.endTime) {
      updateCaptionText(currentSegment.line1, currentSegment.line2)
    }

    if (elapsed >= currentSegment.endTime) {
      currentSegmentIndexRef.current++

      if (currentSegmentIndexRef.current < segments.length) {
        const nextSegment = segments[currentSegmentIndexRef.current]
        const gap = nextSegment.startTime - elapsed

        if (gap > 200) {
          updateCaptionText("", "")
        }
      } else {
        updateCaptionText("", "")
      }
    }
  }, [updateCaptionText])

  const startCaptionPlayback = useCallback(
    (speechMarks: Array<TSentence | TWord | TViseme>) => {
      const segments = prepareCaptions(speechMarks)

      if (segments.length === 0) {
        console.log("[CAPTIONS] No caption segments generated")
        return false
      }

      // console.log(`[CAPTIONS] Starting playback with ${segments.length} segments`)

      captionSegmentsRef.current = segments
      currentSegmentIndexRef.current = 0
      setIsCaptionActive(true)
      captionStartTimeRef.current = Date.now()

      captionIntervalRef.current = setInterval(captionLoop, 16)

      return true
    },
    [prepareCaptions, captionLoop],
  )

  const stopCaptions = useCallback(() => {
    if (captionIntervalRef.current) {
      clearInterval(captionIntervalRef.current)
      captionIntervalRef.current = null
    }
    setIsCaptionActive(false)
    setCaptionText({ line1: "", line2: "" })
    currentSegmentIndexRef.current = 0
    captionSegmentsRef.current = []
  }, [])

  // ===== VISEME ANIMATION FUNCTIONS =====

  const stop = useCallback(() => {
    scheduledTimeoutsRef.current.forEach(clearTimeout)
    scheduledTimeoutsRef.current = []

    if (activeAnimationRef.current) {
      clearTimeout(activeAnimationRef.current)
      activeAnimationRef.current = null
    }

    const currentRive = riveRef.current
    if (currentRive) {
      try {
        INPUT_NAMES.forEach((inputName) => {
          currentRive.setInputState("State Machine 1", inputName, inputName === "CLOSE" ? 100 : 0)
        })
        console.log("Rive inputs reset successfully")
      } catch (error) {
        console.log("Rive component unmounted, skipping input reset:", error)
      }
    }

    currentTargetViseme.current = "CLOSE"
    setIsAnimating(false)
    setCurrentViseme(null)
    stopCaptions()
  }, [stopCaptions])

  const animateToViseme = useCallback(
    (
      targetInput: string,
      duration: number,
      easingType: keyof typeof easingFunctions = "EaseInOutQuad",
    ) => {
      const currentRive = riveRef.current
      if (!currentRive) return

      if (activeAnimationRef.current) {
        clearTimeout(activeAnimationRef.current)
      }

      const previousTarget = currentTargetViseme.current
      currentTargetViseme.current = targetInput

      const previousValues: Record<string, number> = {}
      INPUT_NAMES.forEach((inputName) => {
        previousValues[inputName] = inputName === previousTarget ? 100 : 0
      })

      const startTime = Date.now()
      const easingFunc = easingFunctions[easingType]

      const animate = () => {
        const liveRive = riveRef.current
        if (!liveRive) {
          console.log("Rive component unmounted during animation, stopping")
          activeAnimationRef.current = null
          return
        }

        const elapsed = Date.now() - startTime
        const progress = Math.min(elapsed / duration, 1)

        if (progress >= 1) {
          try {
            INPUT_NAMES.forEach((inputName) => {
              const finalValue = inputName === targetInput ? 100 : 0
              liveRive.setInputState("State Machine 1", inputName, finalValue)
            })
          } catch (error) {
            console.log("Rive component unmounted during final state setting:", error)
            activeAnimationRef.current = null
            return
          }
          activeAnimationRef.current = null
          return
        }

        const easedProgress = easingFunc(progress)

        try {
          INPUT_NAMES.forEach((inputName) => {
            const previousValue = previousValues[inputName]
            const targetValue = inputName === targetInput ? 100 : 0
            const currentValue = Math.round(
              previousValue + (targetValue - previousValue) * easedProgress,
            )

            liveRive.setInputState("State Machine 1", inputName, currentValue)
          })
        } catch (error) {
          console.log("Rive component unmounted during animation frame, stopping:", error)
          activeAnimationRef.current = null
          return
        }

        activeAnimationRef.current = setTimeout(animate, 16)
      }

      animate()
    },
    [],
  )

  const animateSpeech = useCallback(
    async (speechMarkData: string | Array<TSentence | TWord | TViseme>, offsetMs = 0) => {
      const currentRive = riveRef.current
      if (!currentRive) {
        console.error("Rive instance not available")
        return false
      }

      scheduledTimeoutsRef.current.forEach(clearTimeout)
      scheduledTimeoutsRef.current = []

      try {
        let allMarks: Array<TSentence | TWord | TViseme>
        if (typeof speechMarkData === "string") {
          allMarks = speechMarkData.includes("\n")
            ? speechMarkData
                .split("\n")
                .filter((line) => line.trim())
                .map((line) => JSON.parse(line))
            : JSON.parse(speechMarkData)
        } else {
          allMarks = speechMarkData
        }

        startCaptionPlayback(allMarks)

        const visemeMarks = allMarks
          .filter((mark): mark is TViseme => mark.type === NodeType.VISEME)
          .sort((a, b) => a.time - b.time)

        if (visemeMarks.length === 0) {
          console.warn("No viseme marks found")
          return false
        }

        // When resuming partway through the clip, skip visemes that should have
        // already played and start from the one aligned with the current audio
        // position. With offsetMs === 0 this is index 0 (normal full playback).
        const startIndex = (() => {
          const idx = visemeMarks.findIndex((mark) => mark.time >= offsetMs)
          return idx === -1 ? visemeMarks.length : idx
        })()

        console.log(
          `Scheduling ${visemeMarks.length - startIndex} visemes (offset ${offsetMs}ms, skipped ${startIndex})`,
        )

        setIsAnimating(true)
        animationStartTimeRef.current = Date.now() - offsetMs

        // Snap to the viseme that should already be showing at the current offset
        // so the mouth doesn't restart from a closed position mid-word.
        if (startIndex > 0) {
          const elapsedMark = visemeMarks[startIndex - 1]
          const nextMark = visemeMarks[startIndex]
          const interpTime = nextMark ? nextMark.time - elapsedMark.time : 150
          setCurrentViseme({
            value: elapsedMark.value,
            time: elapsedMark.time,
            interpTime,
            riveInput: getRiveInput(elapsedMark.value),
          })
          animateToViseme(getRiveInput(elapsedMark.value), interpTime, "EaseInOutQuad")
        }

        // The whole clip already elapsed — close the mouth and stop.
        if (startIndex >= visemeMarks.length) {
          animateToViseme("CLOSE", 150, "EaseInOutQuad")
          setIsAnimating(false)
          setCurrentViseme(null)
          return true
        }

        // Schedule the remaining (future) visemes, offset so each fires when the
        // audio actually reaches it.
        for (let index = startIndex; index < visemeMarks.length; index++) {
          const mark = visemeMarks[index]
          const nextMark = visemeMarks[index + 1]
          const interpTime = nextMark ? nextMark.time - mark.time : 150
          const riveInput = getRiveInput(mark.value)

          const timeout = setTimeout(() => {
            const liveRive = riveRef.current
            if (!liveRive) return

            setCurrentViseme({
              value: mark.value,
              time: mark.time,
              interpTime,
              riveInput,
            })

            animateToViseme(riveInput, interpTime, "EaseInOutQuad")

            if (index === visemeMarks.length - 1) {
              setTimeout(() => {
                animateToViseme("CLOSE", 150, "EaseInOutQuad")
                setTimeout(() => {
                  setIsAnimating(false)
                  setCurrentViseme(null)
                }, 200)
              }, interpTime + 50)
            }
          }, mark.time - offsetMs)

          scheduledTimeoutsRef.current.push(timeout)
        }

        return true
      } catch (error) {
        console.error("Error playing speech:", error)
        stop()
        return false
      }
    },
    [stop, animateToViseme, startCaptionPlayback],
  )

  React.useEffect(() => {
    return () => {
      if (captionIntervalRef.current) {
        clearInterval(captionIntervalRef.current)
      }
    }
  }, [])

  return {
    animateSpeech,
    stop,
    isAnimating,
    currentViseme,
    animateToViseme,
    captionText,
    isCaptionActive,
  }
}
